import type { SeriesRef } from '../dicomweb';
import type { Layout, ViewerShell } from '../shell';
import { isImageSeries } from '../study';
import type { Viewport } from '../viewport';

/**
 * " Múltiples pantallas" (spec section 2, lib-select-screen): the user picks 1, 2 or 3 screens; screens
 * 2 and 3 are child windows of the same viewer (same URL plus `screen=N`). The windows talk over a
 * BroadcastChannel scoped to the study: the main window assigns each child its series (the study's
 * series continue across screens, cell by cell), can place a series on any screen, and the slice on
 * screen follows between windows showing the same series.
 */

export const SCREEN_TEXT = {
  title: 'Panel de configuración múltiples pantallas:',
  select: 'SELECCIONAR',
  cards: ['1 pantalla', '2 pantallas', '3 pantallas'],
  cardHints: ['Visor en esta ventana', 'Esta ventana y una ventana adicional', 'Esta ventana y dos ventanas adicionales'],
  cancel: 'Cancelar',
  blocked: 'El navegador bloqueó la apertura de pantallas adicionales. Permita las ventanas emergentes para este visor.',
} as const;

const CARD_CLASSES = ['card-one-screen', 'card-two-screens', 'card-three-screens'] as const;
const SCREEN_PARAM = 'screen';
const TOKEN_PARAM = 'token';
/** Window property the child screens call on their opener to get the ticket (config.ts reads it). */
export const CHILD_TICKET_HANDOFF = '__viewerTicketForChild';
const MAX_SCREENS = 3;
const CHANNEL_PREFIX = 'viewer-screens:';
const WINDOW_NAME_PREFIX = 'viewer-screen-';

type ScreenMessage =
  | { type: 'hello'; screen: number }
  | { type: 'assign'; screen: number; layout: Layout; series: Array<string | null> }
  | { type: 'place'; screen: number; cell: number; series: string }
  | { type: 'slice'; from: number; series: string; index: number }
  | { type: 'close' };

let shellRef: ViewerShell | null = null;
let channel: BroadcastChannel | null = null;
/** 1 for the main window, 2 / 3 for child windows. */
let screenNumber = 1;
let screenCount = 1;
const children = new Map<number, Window>();
/** Set while applying a slice received from another window, so it is not echoed back. */
let applyingRemote = false;

function requireShell(): ViewerShell {
  if (!shellRef) throw new Error('multiscreen.install() must run first');
  return shellRef;
}

export function currentScreen(): number {
  return screenNumber;
}

export function screensOpen(): number {
  return screenCount;
}

function post(message: ScreenMessage): void {
  channel?.postMessage(message);
}

function findSeries(shell: ViewerShell, uid: string | null): SeriesRef | undefined {
  if (!uid) return undefined;
  return shell.studies().flatMap((s) => s.series).find((s) => s.seriesInstanceUID === uid);
}

/** Series of screen `screen`: the opened study's viewable series continue from one screen to the next. */
function assignmentFor(shell: ViewerShell, screen: number): Array<string | null> {
  const layout = shell.layout();
  const cells = layout.rows * layout.columns;
  const all = shell.study().series.filter(isImageSeries);
  return Array.from({ length: cells }, (_, cell) => {
    if (all.length === 0) return null;
    return all[((screen - 1) * cells + cell) % all.length].seriesInstanceUID;
  });
}

/**
 * URL of child screen `screen`: this viewer's address with `screen=N`. The ticket is NOT put in the
 * URL (it would land in the child's history); the child reads it from this window through
 * `window.opener` at boot (see readLaunchParams in config.ts).
 */
export function screenUrl(screen: number): string {
  const url = new URL(window.location.href);
  url.searchParams.set(SCREEN_PARAM, String(screen));
  url.searchParams.delete(TOKEN_PARAM);
  return url.toString();
}

/** Lets child screens (same origin, opened by this window) take the ticket without a URL. */
function exposeTicketToChildren(shell: ViewerShell): void {
  // Read when the child asks: a ticket renewed since this window opened is the one handed over.
  const ticket = (): string | null => {
    const auth = shell.ctx.client.headers().Authorization;
    return auth?.startsWith('Bearer ') ? auth.slice('Bearer '.length) : null;
  };
  Object.defineProperty(window, CHILD_TICKET_HANDOFF, { value: ticket, configurable: true, enumerable: false });
}

function closeChildren(): void {
  post({ type: 'close' });
  children.forEach((child) => {
    try {
      child.close();
    } catch {
      // The child may already be gone.
    }
  });
  children.clear();
}

/**
 * Uses `count` screens: this window plus `count - 1` child windows. A blocked popup stops the
 * sequence with a toast; the screens opened so far stay.
 */
export function selectScreens(count: number): number {
  const shell = requireShell();
  if (screenNumber !== 1) return screenCount;
  const target = Math.min(Math.max(Math.round(count), 1), MAX_SCREENS);
  closeChildren();
  screenCount = 1;
  for (let screen = 2; screen <= target; screen++) {
    const child = window.open(screenUrl(screen), `${WINDOW_NAME_PREFIX}${screen}`, 'popup');
    if (!child) {
      shell.toast(SCREEN_TEXT.blocked, 'warning');
      break;
    }
    children.set(screen, child);
    screenCount = screen;
  }
  document.documentElement.dataset.screens = String(screenCount);
  return screenCount;
}

/** Places a series in a cell of any screen (screen 1 is this window). */
export function placeSeries(screen: number, cell: number, seriesUID: string): void {
  const shell = requireShell();
  if (screen === screenNumber) {
    const series = findSeries(shell, seriesUID);
    if (series) void shell.showSeriesIn(cell, series);
    return;
  }
  post({ type: 'place', screen, cell, series: seriesUID });
}

async function applyAssignment(shell: ViewerShell, layout: Layout, series: Array<string | null>): Promise<void> {
  const current = shell.layout();
  if (current.rows !== layout.rows || current.columns !== layout.columns) shell.setLayout(layout);
  await Promise.all(series.map((uid, cell) => {
    const found = findSeries(shell, uid);
    const shown = shell.viewports()[cell]?.currentStack?.series ?? shell.viewports()[cell]?.customSeries;
    return found && shown?.seriesInstanceUID !== found.seriesInstanceUID ? shell.showSeriesIn(cell, found) : Promise.resolve();
  }));
  document.documentElement.dataset.screenAssigned = 'true';
}

function applySlice(shell: ViewerShell, seriesUID: string, index: number): void {
  applyingRemote = true;
  try {
    for (const viewport of shell.viewports()) {
      const stack = viewport.currentStack;
      if (!stack || stack.series.seriesInstanceUID !== seriesUID || viewport.currentIndex() === index) continue;
      if (index >= 0 && index < stack.imageIds.length) window.cornerstoneTools.scrollToIndex(viewport.element, index);
    }
  } finally {
    applyingRemote = false;
  }
}

function onMessage(shell: ViewerShell, message: ScreenMessage): void {
  switch (message.type) {
    case 'hello':
      if (screenNumber === 1 && message.screen > 1) {
        post({ type: 'assign', screen: message.screen, layout: shell.layout(), series: assignmentFor(shell, message.screen) });
      }
      return;
    case 'assign':
      if (message.screen === screenNumber) void applyAssignment(shell, message.layout, message.series);
      return;
    case 'place':
      if (message.screen === screenNumber) placeSeries(message.screen, message.cell, message.series);
      return;
    case 'slice':
      if (message.from !== screenNumber) applySlice(shell, message.series, message.index);
      return;
    case 'close':
      if (screenNumber > 1) window.close();
      return;
  }
}

function watchSlices(shell: ViewerShell, viewports: Viewport[]): void {
  for (const viewport of viewports) {
    viewport.element.addEventListener(window.cornerstone.EVENTS.NEW_IMAGE, () => {
      if (applyingRemote || screenCount < 2 && screenNumber === 1) return;
      const series = viewport.currentStack?.series.seriesInstanceUID;
      const index = viewport.currentIndex();
      if (series && index !== undefined && shell.activeViewport() === viewport) post({ type: 'slice', from: screenNumber, series, index });
    });
  }
}

/** "Panel de configuración múltiples pantallas:" with the three cards. */
export function openScreenDialog(shell: ViewerShell): void {
  document.querySelectorAll('.cmd-dialog-backdrop').forEach((node) => node.remove());
  const backdrop = document.createElement('div');
  backdrop.className = 'cmd-dialog-backdrop';
  const dialog = document.createElement('div');
  dialog.className = 'cmd-dialog select-screen-dialog';
  dialog.setAttribute('role', 'dialog');
  dialog.setAttribute('aria-modal', 'true');
  dialog.setAttribute('aria-labelledby', 'select-screen-title');
  dialog.tabIndex = -1;
  const title = document.createElement('h2');
  title.className = 'cmd-dialog-title';
  title.id = 'select-screen-title';
  title.textContent = SCREEN_TEXT.title;
  const cards = document.createElement('div');
  cards.className = 'cmd-dialog-body select-screen-cards';
  const close = () => {
    backdrop.remove();
    document.removeEventListener('keydown', onKey, true);
  };
  const onKey = (event: KeyboardEvent) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    close();
  };
  CARD_CLASSES.forEach((className, index) => {
    const card = document.createElement('div');
    card.className = `select-screen-card ${className}`;
    card.classList.toggle('active', index + 1 === screenCount);
    const screens = document.createElement('div');
    screens.className = 'select-screen-preview';
    for (let i = 0; i <= index; i++) screens.appendChild(document.createElement('span'));
    const name = document.createElement('div');
    name.className = 'select-screen-name';
    name.textContent = SCREEN_TEXT.cards[index];
    const hint = document.createElement('div');
    hint.className = 'select-screen-hint';
    hint.textContent = SCREEN_TEXT.cardHints[index];
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'cmd-dialog-button primary select-screen-button';
    button.textContent = SCREEN_TEXT.select;
    button.addEventListener('click', () => {
      close();
      selectScreens(index + 1);
    });
    card.append(screens, name, hint, button);
    cards.appendChild(card);
  });
  const actions = document.createElement('div');
  actions.className = 'cmd-dialog-actions';
  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.className = 'cmd-dialog-button';
  cancel.textContent = SCREEN_TEXT.cancel;
  cancel.addEventListener('click', close);
  actions.appendChild(cancel);
  dialog.append(title, cards, actions);
  backdrop.appendChild(dialog);
  backdrop.addEventListener('mousedown', (event) => {
    if (event.target === backdrop) close();
  });
  document.addEventListener('keydown', onKey, true);
  shell.regions.overlayLayer.appendChild(backdrop);
  dialog.querySelector<HTMLButtonElement>('.select-screen-button')?.focus();
}

export function install(shell: ViewerShell): void {
  shellRef = shell;
  exposeTicketToChildren(shell);
  const requested = Number(new URL(window.location.href).searchParams.get(SCREEN_PARAM) ?? '1');
  screenNumber = Number.isInteger(requested) && requested >= 1 && requested <= MAX_SCREENS ? requested : 1;
  document.documentElement.dataset.screen = String(screenNumber);
  shell.on('layout-changed', ({ viewports }) => watchSlices(shell, viewports));
  if (typeof BroadcastChannel === 'undefined') return;
  channel = new BroadcastChannel(`${CHANNEL_PREFIX}${shell.study().studyInstanceUID}`);
  channel.addEventListener('message', (event: MessageEvent<ScreenMessage>) => onMessage(shell, event.data));
  if (screenNumber > 1) {
    shell.setLeftDrawer(false);
    // Ask the main window for this screen's series once the first layout exists.
    queueMicrotask(() => post({ type: 'hello', screen: screenNumber }));
  } else {
    window.addEventListener('pagehide', closeChildren);
  }
}
